// import React from "react";
// import {
//   Container,
//   Typography,
//   Box,
//   useTheme,
//   alpha,
//   Chip
// } from "@mui/material";

// const Terms = () => {
//   const theme = useTheme();

//   return (
//     <Container maxWidth="lg" sx={{ py: 8 }}>
//       <Box sx={{ textAlign: "center", mb: 6 }}>
//         <Chip 
//           label="Page Category" 
//           sx={{ 
//             mb: 3,
//             background: alpha(theme.palette.primary.main, 0.1),
//             color: theme.palette.primary.dark,
//             fontWeight: 600,
//             fontSize: "0.85rem"
//           }}
//         />
//         <Typography
//           variant="h2"
//           sx={{
//             fontWeight: 700,
//             color: theme.palette.text.primary,
//             mb: 3,
//             fontSize: { xs: "2.5rem", md: "3rem" }
//           }}
//         >
//           Page Title
//         </Typography>
//         <Typography
//           variant="h6"
//           sx={{
//             color: theme.palette.text.secondary,
//             maxWidth: "800px",
//             mx: "auto",
//             lineHeight: 1.6,
//             fontSize: "1.1rem"
//           }}
//         >
//           Page content goes here. This is a placeholder that you can replace with 
//           actual content specific to this page.
//         </Typography>
//       </Box>

//       {/* Add more sections as needed */}
//     </Container>
//   );
// };

// export default Terms;

import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Link
} from "@mui/material";

const Terms = () => {
  const theme = useTheme();

  // Helper to create section IDs for anchor links
  const sections = [
    { id: "introduction", title: "1. Introduction" },
    { id: "definitions", title: "2. Definitions" },
    { id: "eligibility", title: "3. Eligibility" },
    { id: "account", title: "4. Account Registration" },
    { id: "host-obligations", title: "5. Host Obligations" },
    { id: "cleaner-obligations", title: "6. Cleaner Obligations" },
    { id: "booking-payment", title: "7. Booking and Payment Process" },
    { id: "cancellation", title: "8. Cancellation and Refunds" },
    { id: "user-content", title: "9. User Content" },
    { id: "prohibited-conduct", title: "10. Prohibited Conduct" },
    { id: "fees", title: "11. Fees and Commissions" },
    { id: "disclaimers", title: "12. Disclaimers and Limitation of Liability" },
    { id: "indemnification", title: "13. Indemnification" },
    { id: "termination", title: "14. Termination" },
    { id: "dispute-resolution", title: "15. Dispute Resolution" },
    { id: "governing-law", title: "16. Governing Law" },
    { id: "changes", title: "17. Changes to Terms" },
    { id: "miscellaneous", title: "18. Miscellaneous" },
    { id: "contact", title: "19. Contact Information" }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Legal"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Terms and Conditions
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "700px",
            mx: "auto",
            mt: 2,
            fontSize: "0.95rem"
          }}
        >
          Welcome to FreshSweeper. These Terms and Conditions govern your use of our platform.
          Please read them carefully before using our services.
        </Typography>
      </Box>

      {/* Main content with optional sidebar table of contents */}
      <Grid container spacing={4}>
        {/* Sidebar - Table of Contents (hidden on mobile) */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 100,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              On this page
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, listStyle: "none" }}>
              {sections.map((section) => (
                <Box component="li" key={section.id} sx={{ mb: 0.5 }}>
                  <Link
                    href={`#${section.id}`}
                    underline="hover"
                    sx={{
                      fontSize: "0.85rem",
                      color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.primary.main },
                      textDecoration: "none"
                    }}
                  >
                    {section.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main content area */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* 1. Introduction */}
            <Box id="introduction" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                1. Introduction
              </Typography>
              <Typography variant="body1" paragraph>
                Welcome to FreshSweeper. These Terms and Conditions ("Terms") govern your use of
                the FreshSweeper website, mobile application, and related services (collectively,
                the "Platform") provided by FreshSweeper Inc. ("Company", "we", "us", "our").
              </Typography>
              <Typography variant="body1">
                By accessing or using the Platform, you agree to be bound by these Terms.
                If you do not agree, please do not use the Platform.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 2. Definitions */}
            <Box id="definitions" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                2. Definitions
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li><Typography variant="body1"><strong>"Host"</strong> – a user who lists a property for cleaning services.</Typography></li>
                <li><Typography variant="body1"><strong>"Cleaner"</strong> – a user who provides cleaning services through the Platform.</Typography></li>
                <li><Typography variant="body1"><strong>"Booking"</strong> – a confirmed arrangement between a Host and a Cleaner for cleaning services.</Typography></li>
                <li><Typography variant="body1"><strong>"Service Fee"</strong> – the fee charged by FreshSweeper for facilitating a Booking.</Typography></li>
                <li><Typography variant="body1"><strong>"Content"</strong> – any text, images, reviews, or other material posted by users.</Typography></li>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 3. Eligibility */}
            <Box id="eligibility" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                3. Eligibility
              </Typography>
              <Typography variant="body1">
                You must be at least 18 years old and capable of forming a binding contract to use
                the Platform. By registering, you represent that all information you provide is
                accurate, current, and complete.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 4. Account Registration */}
            <Box id="account" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                4. Account Registration
              </Typography>
              <Typography variant="body1" paragraph>
                You are responsible for maintaining the confidentiality of your login credentials.
                You are solely responsible for all activities that occur under your account.
                Notify us immediately of any unauthorized use of your account.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 5. Host Obligations */}
            <Box id="host-obligations" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                5. Host Obligations
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                5.1 Property Listings
              </Typography>
              <Typography variant="body1" paragraph>
                Hosts must provide accurate descriptions, address, room details, and cleaning requirements.
                Hosts must have the legal right to permit cleaning services on the property.
                FreshSweeper reserves the right to remove any listing that violates these Terms.
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                5.2 Cleaning Instructions
              </Typography>
              <Typography variant="body1" paragraph>
                Hosts must provide clear safety and access instructions. Hosts must ensure the property
                is safe for cleaners (no hazardous materials, aggressive pets, etc.).
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                5.3 Payment
              </Typography>
              <Typography variant="body1" paragraph>
                Hosts agree to pay the quoted cleaning fee plus any applicable Service Fee.
                Payment is processed through our secure payment provider. Hosts authorize us to
                charge the provided payment method.
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                5.4 Cancellation
              </Typography>
              <Typography variant="body1">
                Host cancellation policies are displayed during booking. Cancellation fees apply
                as outlined in the Platform.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 6. Cleaner Obligations */}
            <Box id="cleaner-obligations" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                6. Cleaner Obligations
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                6.1 Performance
              </Typography>
              <Typography variant="body1" paragraph>
                Cleaners must perform the agreed cleaning services to a professional standard.
                Cleaners must arrive on time, bring necessary supplies (unless otherwise agreed),
                and follow Host instructions.
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                6.2 Conduct
              </Typography>
              <Typography variant="body1" paragraph>
                Cleaners must act professionally, respect the Host’s property, and not engage in any
                illegal or unsafe activities.
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                6.3 Payment
              </Typography>
              <Typography variant="body1" paragraph>
                Cleaners will receive the cleaning fee minus any Platform commission or adjustment
                (e.g., penalties for late cancellation). Payment will be made via the payment method
                selected by the Cleaner.
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                6.4 Background Checks
              </Typography>
              <Typography variant="body1">
                FreshSweeper may, at its discretion, conduct background checks on Cleaners.
                Cleaners agree to provide any necessary consent for such checks.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 7. Booking and Payment Process */}
            <Box id="booking-payment" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                7. Booking and Payment Process
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Booking Request</strong> – Host selects a date, time, and service. The request
                is sent to one or more Cleaners.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Acceptance</strong> – A Cleaner may accept the request, forming a binding
                contract between Host and Cleaner.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Payment Hold</strong> – The Host’s payment method is authorized at the time
                of booking. Funds are held until the service is completed and approved.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Release of Payment</strong> – After the service is completed and the Host has
                had a reasonable time to review (e.g., 48 hours), funds are released to the Cleaner
                (minus fees).
              </Typography>
              <Typography variant="body1">
                <strong>Disputes</strong> – If the Host disputes the quality of the cleaning,
                the matter will be reviewed by FreshSweeper, and we may hold funds pending resolution.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 8. Cancellation and Refunds */}
            <Box id="cancellation" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                8. Cancellation and Refunds
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Host Cancellation</strong> – A Host may cancel a Booking subject to the
                cancellation policy shown at booking. Fees may apply (e.g., 25% – 100% of the cleaning
                fee depending on notice period).
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Cleaner Cancellation</strong> – A Cleaner who cancels late or without a valid
                reason may receive a penalty (e.g., reduced rating, temporary suspension, or financial
                penalty).
              </Typography>
              <Typography variant="body1">
                <strong>Refunds</strong> – If a Host cancels within the permitted window, a refund will
                be issued minus any applicable fee. If the Cleaner fails to perform, FreshSweeper may
                refund the Host and seek reimbursement from the Cleaner.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 9. User Content */}
            <Box id="user-content" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                9. User Content
              </Typography>
              <Typography variant="body1" paragraph>
                You retain ownership of Content you post, but grant FreshSweeper a non‑exclusive,
                royalty‑free, worldwide license to use, display, and promote that Content on the Platform.
              </Typography>
              <Typography variant="body1" paragraph>
                You may not post false, defamatory, obscene, or illegal Content.
              </Typography>
              <Typography variant="body1">
                We reserve the right to remove any Content that violates these Terms.
              </Typography>
            </Box>

            {/* 9a. Mobile Messaging Terms - NEW SECTION */}
              <Divider sx={{ my: 3 }} />
              <Box id="mobile-messaging" sx={{ scrollMarginTop: 80, mb: 4 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                  9a. Mobile Messaging Terms
                </Typography>
                <Typography variant="body1" paragraph>
                  By providing your phone number and opting in to receive SMS or MMS messages from FreshSweeper, you agree to receive transactional and promotional messages at that number. You understand that your consent is not a condition of purchasing any goods or services from us.
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Message frequency varies.</strong> Standard message and data rates may apply. You may opt out of receiving text messages at any time by replying <strong>STOP</strong> to any message you receive. For help, reply <strong>HELP</strong> or contact us at support@freshsweeper.com.
                </Typography>
                <Typography variant="body1">
                  We may change or suspend our text messaging service at any time. We will not be liable for any delays or failures in receipt of any SMS messages. For complete details, please see our <Link href="/privacyp">Privacy Policy</Link>.
                </Typography>
              </Box>

            <Divider sx={{ my: 3 }} />

            {/* 10. Prohibited Conduct */}
            <Box id="prohibited-conduct" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                10. Prohibited Conduct
              </Typography>
              <Typography variant="body1" paragraph>
                You agree not to:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li><Typography variant="body1">Use the Platform for any illegal purpose.</Typography></li>
                <li><Typography variant="body1">Harass, threaten, or discriminate against any other user.</Typography></li>
                <li><Typography variant="body1">Circumvent our payment system (e.g., paying a Cleaner outside the Platform).</Typography></li>
                <li><Typography variant="body1">Reverse engineer or attempt to extract the source code of our software.</Typography></li>
                <li><Typography variant="body1">Create multiple accounts or provide false information.</Typography></li>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 11. Fees and Commissions */}
            <Box id="fees" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                11. Fees and Commissions
              </Typography>
              <Typography variant="body1" paragraph>
                FreshSweeper charges a Service Fee to Hosts (e.g., a percentage of the cleaning fee).
                The exact amount will be displayed before booking.
              </Typography>
              <Typography variant="body1" paragraph>
                Cleaners may be charged a commission or transaction fee, also displayed before
                they accept a Booking.
              </Typography>
              <Typography variant="body1" paragraph>
                All fees are non‑refundable except as otherwise stated in these Terms or required
                by law.
              </Typography>
              <Typography variant="body1">
                We may change fees upon 30 days’ notice posted on the Platform.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 12. Disclaimers and Limitation of Liability */}
            <Box id="disclaimers" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                12. Disclaimers and Limitation of Liability
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>"As Is" Basis</strong> – The Platform is provided on an "AS IS" and "AS AVAILABLE"
                basis. We do not warrant that the Platform will be uninterrupted or error‑free.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>No Liability for User Conduct</strong> – FreshSweeper is not responsible for
                the actions, omissions, or quality of work of any Host or Cleaner. We do not guarantee
                that a Cleaner will perform satisfactorily.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Limitation of Liability</strong> – To the maximum extent permitted by law,
                FreshSweeper’s total liability for any claim arising out of these Terms or your use
                of the Platform shall not exceed the amount you paid (or earned) through the Platform
                in the three months preceding the claim. In no event shall we be liable for
                consequential, incidental, or punitive damages.
              </Typography>
              <Typography variant="body1">
                Some jurisdictions do not allow the exclusion of certain warranties or limitations
                of liability, so the above may not apply to you.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 13. Indemnification */}
            <Box id="indemnification" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                13. Indemnification
              </Typography>
              <Typography variant="body1">
                You agree to indemnify and hold FreshSweeper and its officers, directors, employees,
                and agents harmless from any claim, demand, loss, or expense (including reasonable
                attorneys’ fees) arising out of your violation of these Terms, your use of the Platform,
                or your interaction with other users.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 14. Termination */}
            <Box id="termination" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                14. Termination
              </Typography>
              <Typography variant="body1" paragraph>
                Either party may terminate your account at any time with or without cause.
              </Typography>
              <Typography variant="body1" paragraph>
                We may suspend or terminate your access immediately if you breach these Terms.
              </Typography>
              <Typography variant="body1" paragraph>
                Upon termination, you must cease using the Platform, and any pending payments will
                be settled according to the cancellation policy.
              </Typography>
              <Typography variant="body1">
                Sections that by their nature should survive termination (e.g., payment obligations,
                disclaimers, indemnification) will survive.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 15. Dispute Resolution */}
            <Box id="dispute-resolution" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                15. Dispute Resolution
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Informal Resolution</strong> – Before filing a claim, you agree to contact us
                and attempt to resolve the dispute informally.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Binding Arbitration</strong> – Any dispute arising from these Terms or your
                use of the Platform shall be resolved through binding arbitration administered by
                [American Arbitration Association / JAMS] in accordance with its rules. The arbitration
                shall take place in [City, State], and the arbitrator’s decision may be entered in
                any court of competent jurisdiction.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Class Action Waiver</strong> – You agree to bring any claim only in your
                individual capacity and not as a plaintiff or class member in any class action or
                representative proceeding.
              </Typography>
              <Typography variant="body1">
                <strong>Small Claims</strong> – You may bring a claim in small claims court if it
                qualifies, instead of arbitration.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 16. Governing Law */}
            <Box id="governing-law" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                16. Governing Law
              </Typography>
              <Typography variant="body1">
                These Terms shall be governed by and construed in accordance with the laws of
                New York, USA, without regard to its conflict of laws principles.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 17. Changes to Terms */}
            <Box id="changes" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                17. Changes to Terms
              </Typography>
              <Typography variant="body1">
                We may update these Terms from time to time. We will notify you of material changes
                by posting a notice on the Platform or sending an email. Your continued use after
                the effective date constitutes acceptance of the revised Terms.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 18. Miscellaneous */}
            <Box id="miscellaneous" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                18. Miscellaneous
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Entire Agreement</strong> – These Terms constitute the entire agreement
                between you and FreshSweeper regarding your use of the Platform.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Severability</strong> – If any provision is found unenforceable, the remaining
                provisions shall remain in full force and effect.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Waiver</strong> – Our failure to enforce any right or provision does not
                waive that right or provision.
              </Typography>
              <Typography variant="body1">
                <strong>Assignment</strong> – You may not assign your rights under these Terms without
                our prior written consent. We may assign them without restriction.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 19. Contact Information */}
            <Box id="contact" sx={{ scrollMarginTop: 80, mb: 2 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                19. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                For questions or complaints, please contact us at:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                <strong>FreshSweeper Inc.</strong><br />
                Email: legal@freshsweeper.com<br />
                Phone: +1 973 380 4757<br />
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Terms;